import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, spacing } from '@/theme/tokens';

const BENEFITS: Array<{ icon: keyof typeof Ionicons.glyphMap; key: string }> = [
  { icon: 'folder-open-outline', key: 'auth.benefitPolicies' },
  { icon: 'person-outline', key: 'auth.benefitAgent' },
  { icon: 'refresh-outline', key: 'auth.benefitRenewals' },
];

/**
 * Post-login linking — mock: po_cz_polisy/screen.png. SINGLE_MASKED confirms in
 * one tap ("Czy to Ty?"); MULTIPLE asks for PESEL, which the backend only uses
 * to pick between phone-matched candidates. Anything unclear goes to the human
 * fallback (/auth/help). Skipping is always one tap: linking is an offer, not a gate.
 */
export default function AuthConnectScreen() {
  const { t } = useTranslation();
  const matchStatus = useAuthStore((s) => s.matchStatus);
  const maskedMatch = useAuthStore((s) => s.maskedMatch);
  const confirmLink = useAuthStore((s) => s.confirmLink);
  const linkByPesel = useAuthStore((s) => s.linkByPesel);

  const [pesel, setPesel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const multiple = matchStatus === 'MULTIPLE';
  const done = () => router.dismissTo('/(tabs)/policies');

  const onConnect = async () => {
    setBusy(true);
    setError(null);
    const result = multiple ? await linkByPesel(pesel) : await confirmLink();
    setBusy(false);
    if (result.ok) {
      done();
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('home.appName')} />

        <View style={{ alignItems: 'center', marginTop: spacing.md }}>
          <View
            style={{
              width: 96,
              height: 96,
              borderRadius: radius.full,
              backgroundColor: colors.infoBg,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons name="shield-half-outline" size={48} color={colors.primary} />
          </View>
        </View>

        <AppText variant="headlineLg" color={colors.navy} style={{ textAlign: 'center' }}>
          {t('auth.connectTitle')}
        </AppText>
        <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
          {t('auth.connectSubtitle')}
        </AppText>

        {/* Masked auto-match preview — only an initial and a count, by design. */}
        {matchStatus === 'SINGLE_MASKED' && maskedMatch && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <Ionicons name="sparkles-outline" size={22} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('auth.matchFound', { name: maskedMatch.name })}
              </AppText>
              <AppText variant="bodyMd">
                {t('auth.matchPolicies', { count: maskedMatch.policiesCount })}
              </AppText>
            </View>
          </Card>
        )}

        {/* MULTIPLE: PESEL picks between the phone-matched candidates. */}
        {multiple && (
          <Card style={{ gap: spacing.sm }}>
            <AppText variant="bodyMd">{t('auth.multipleInfo')}</AppText>
            <TextField
              label={t('auth.peselLabel')}
              value={pesel}
              onChangeText={(text) => setPesel(text.replace(/\D/g, '').slice(0, 11))}
              placeholder="___________"
              keyboardType="number-pad"
            />
          </Card>
        )}

        {BENEFITS.map((benefit) => (
          <Card key={benefit.key} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
            <IconTile icon={benefit.icon} bg={colors.primaryTile} fg={colors.primary} size={52} />
            <AppText variant="labelLg" style={{ flex: 1 }}>
              {t(benefit.key)}
            </AppText>
          </Card>
        ))}

        {error && (
          <AppText variant="bodyMd" color={colors.warning} style={{ textAlign: 'center' }}>
            {error}
          </AppText>
        )}

        <PrimaryButton
          label={t('auth.connect')}
          disabled={busy || (multiple && pesel.length !== 11)}
          onPress={onConnect}
        />
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/auth/help')}
          style={{ minHeight: 44, justifyContent: 'center' }}
        >
          <AppText variant="labelLg" color={colors.navy} style={{ textAlign: 'center' }}>
            {t('auth.needHelp')}
          </AppText>
        </Pressable>
        <Pressable accessibilityRole="button" onPress={done} style={{ minHeight: 44, justifyContent: 'center' }}>
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('auth.skipForNow')}
          </AppText>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
